/**
 * ReportContactModal — unified component for three contexts:
 *
 *   <ReportContactModal context="silo_report" siloId={...} siloLabel="Cosmic Thought" open={...} onClose={...} />
 *   <ReportContactModal context="user_report" targetUserId={...} targetUserLabel="alex" open={...} onClose={...} />
 *   <ReportContactModal context="general" open={...} onClose={...} />
 *
 * Same form, different invocation. Anonymous-friendly. POSTs to
 * /api/admin-reports — backend emails the super admin and stores a row
 * for triage.
 *
 * Jun 2026 — added a "Report a bug" type and per-issue PRE-FILLED guidance.
 * Picking an issue type drops a friendly template into the description box
 * (with a screenshot nudge) so people know exactly what to tell us. We only
 * auto-fill when the box is empty or still holds the previous template, so
 * we never clobber something the person typed.
 */
import { useState } from "react";
import { X, PaperPlaneTilt, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ISSUE_OPTIONS = {
  silo_report: [
    ["vulgar_offensive", "Vulgar / offensive content"],
    ["spam", "Spam"],
    ["misinformation", "Misinformation"],
    ["impersonation", "Impersonation"],
    ["harassment", "Harassment"],
    ["other", "Other"],
  ],
  user_report: [
    ["harassment", "Harassment"],
    ["impersonation", "Impersonation"],
    ["spam", "Spam"],
    ["other", "Other"],
  ],
  general: [
    ["bug", "Report a bug / something's broken"],
    ["billing", "Billing question"],
    ["feature_request", "Feature request / idea"],
    ["other", "Something else"],
  ],
};

// Pre-filled guidance per issue type. The first line is a prompt; the bullets
// are blanks the person fills in. Bug + billing include a screenshot nudge.
const GUIDANCE = {
  bug: "Tell me what broke:\n• What were you doing when it happened?\n• What did you expect to happen?\n• What actually happened instead?\n• Page or link where it happened:\n\nTip: a screenshot helps a lot — describe what you saw, or paste a link to an image.",
  billing: "Your billing question:\n• What charge or amount is this about?\n• Date of the charge (if you know it):\n• What you expected:\n\nTip: a screenshot of the charge or receipt helps me sort it out faster.",
  feature_request: "Your idea:\n• What would you like to see?\n• Why would it help you?\n",
  vulgar_offensive: "What's offensive here?\n• What did you see:\n• Where (which silo or page):\n",
  spam: "What looks like spam?\n• What did you see:\n• Where:\n",
  misinformation: "What's inaccurate?\n• The claim that's wrong:\n• Why it's wrong:\n",
  impersonation: "Who's being impersonated?\n• The account or silo:\n• Who they're pretending to be:\n",
  harassment: "What happened?\n• What was said or done:\n• Who, and where:\n",
  other: "",
};

const CONTEXT_TITLES = {
  silo_report: "Report this silo",
  user_report: "Report this user",
  general: "Help & report a problem",
};

const CONTEXT_INTRO = {
  silo_report: "Tell us what's wrong with this silo. We review every report. Anonymous submissions are accepted.",
  user_report: "Tell us what's wrong with this user. Anonymous submissions are accepted.",
  general: "Reach the team directly — report a bug, ask about billing, or share an idea. Pick a topic below and we'll guide you.",
};

export default function ReportContactModal({
  open, onClose,
  context = "general",
  siloId = null, siloLabel = null,
  targetUserId = null, targetUserLabel = null,
}) {
  const firstType = ISSUE_OPTIONS[context][0][0];
  const [issueType, setIssueType] = useState(firstType);
  const [description, setDescription] = useState(GUIDANCE[firstType] || "");
  const [reporterEmail, setReporterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  // Swap the issue type. Drop the new template in ONLY when the box is empty
  // or still holds the previous template (so we never wipe real input).
  const applyIssueType = (newType) => {
    const prevTmpl = GUIDANCE[issueType] || "";
    const nextTmpl = GUIDANCE[newType] || "";
    setDescription((prev) => (!prev.trim() || prev === prevTmpl ? nextTmpl : prev));
    setIssueType(newType);
  };

  const submit = async () => {
    if (description.trim().length < 10) {
      toast.error("Please describe the issue (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin-reports", {
        context,
        issue_type: issueType,
        description: description.trim(),
        reporter_email: reporterEmail.trim() || null,
        silo_id: siloId,
        target_user_id: targetUserId,
      });
      toast.success("Sent to admin. Thanks for the heads-up.");
      setDescription(GUIDANCE[firstType] || "");
      setIssueType(firstType);
      setReporterEmail("");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose} data-testid="report-contact-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white border hairline rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b hairline flex items-center gap-2">
          <Warning size={18} weight="duotone" className="text-[#C16E5A]" />
          <div className="font-serif-display text-lg flex-1">{CONTEXT_TITLES[context]}</div>
          <button onClick={onClose} data-testid="report-contact-close"
            className="text-[#6B6A68] hover:text-[#1C1B1A]"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-xs text-[#6B6A68]">{CONTEXT_INTRO[context]}</p>

          {(siloLabel || targetUserLabel) && (
            <div className="text-[11px] text-[#6B6A68] bg-[#FDFCF8] border hairline rounded p-2">
              {siloLabel && <>Silo: <span className="text-[#1C1B1A]">{siloLabel}</span></>}
              {targetUserLabel && <>User: <span className="text-[#1C1B1A]">{targetUserLabel}</span></>}
            </div>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Topic</span>
            <select value={issueType} onChange={(e) => applyIssueType(e.target.value)}
              data-testid="report-contact-issue-type"
              className="px-2 py-1.5 rounded border hairline bg-white text-sm">
              {ISSUE_OPTIONS[context].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Description</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What's going on? (10–2000 characters)"
              rows={7} maxLength={2000}
              data-testid="report-contact-description"
              className="px-2 py-1.5 rounded border hairline bg-white text-sm focus:outline-none focus:border-[#4A5D4E] whitespace-pre-wrap"
            />
            <span className="text-[10px] text-[#6B6A68] tabular-nums self-end">{description.length}/2000</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Your email (optional)</span>
            <input type="email" value={reporterEmail} onChange={(e) => setReporterEmail(e.target.value)}
              placeholder="leave blank for anonymous"
              data-testid="report-contact-email"
              className="px-2 py-1.5 rounded border hairline bg-white text-sm focus:outline-none focus:border-[#4A5D4E]" />
            <span className="text-[10px] text-[#6B6A68]">If you'd like a reply, leave your email. Otherwise stay anonymous.</span>
          </label>
        </div>

        <div className="px-5 py-3 border-t hairline flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={submitting}
            data-testid="report-contact-cancel"
            className="text-sm px-3 py-1.5 rounded border hairline text-[#6B6A68] hover:text-[#1C1B1A]">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting || description.trim().length < 10}
            data-testid="report-contact-submit"
            className="text-sm px-3 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
            <PaperPlaneTilt size={12} /> {submitting ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
